/**
 * Double Row Orchestrator
 *
 * Coordinates rendering of double-row multi-unit buildings.
 * Uses edge-based wall rendering with proper deduplication.
 * Two rows of units share a central spine wall.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderUnits, type UnitRenderOptions } from '../composers/RectangularUnitComposer'

/**
 * Building render options for double-row
 */
export interface DoubleRowRenderOptions extends UnitRenderOptions {
  /** Building ID for userData */
  buildingId?: string
  /** Building name for userData */
  buildingName?: string
  /** Position in feet [x, y, z] relative to scene center */
  position?: [number, number, number]
  /** Rotation in degrees */
  rotationDeg?: number
}

/**
 * Render a double-row building from generated config
 *
 * Uses the template's generated building and units data.
 * Units from both Row A and Row B are rendered with their respective offsets.
 * Row A units face north, Row B units face south (rotated 180°).
 */
export function renderDoubleRowBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: DoubleRowRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    ...unitOptions
  } = options

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'double_row',
    unitCount: units.length,
  }

  // Get colors from building settings
  const floorColor = building.settings?.floor_color || unitOptions.floorColor
  const wallColor = building.settings?.wall_color || unitOptions.wallColor

  // Render all units using the composer
  // The composer handles edge-based wall rendering with proper deduplication
  // Both Row A and Row B units are included with their offset_x and offset_y values
  const unitsGroup = renderUnits(units, {
    ...unitOptions,
    floorColor,
    wallColor,
    buildingName: buildingName || building.name,
  })

  // Center building on origin
  // Double-row buildings have depth from two rows plus shared wall
  const buildingCenterX = building.exterior_width / 2
  const buildingCenterZ = building.exterior_depth / 2
  // X centering: units have positive X offsets, shift left (negative) to center
  unitsGroup.position.x = -buildingCenterX * FEET_TO_METERS
  // Z centering: units have NEGATED offset_y (negative Z), shift forward (positive) to center
  unitsGroup.position.z = +buildingCenterZ * FEET_TO_METERS

  group.add(unitsGroup)

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)  // Convert radians to degrees
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180  // Convert back to radians
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}

/**
 * Render a double-row building from config object
 * Convenience wrapper for database-loaded data
 */
export function renderDoubleRowFromConfig(
  config: {
    building: GeneratedBuilding
    units: GeneratedUnit[]
    position: [number, number, number]
    rotation?: number
    buildingId?: string
  },
  options: Omit<DoubleRowRenderOptions, 'position' | 'rotationDeg' | 'buildingId'> = {}
): THREE.Group {
  return renderDoubleRowBuilding(
    config.building,
    config.units,
    {
      ...options,
      buildingId: config.buildingId,
      buildingName: config.building.name,
      position: config.position,
      rotationDeg: config.rotation,
    }
  )
}
